import type { SupabaseClient } from '@supabase/supabase-js'

const ADM_NOTAS_BUCKET_ENV = import.meta.env.VITE_ADM_NOTAS_STORAGE_BUCKET?.trim()

export function getSupabaseHostFromEnvForHelp(): string {
  const u = import.meta.env.VITE_SUPABASE_URL || ''
  try {
    return new URL(u).host || '(VITE_SUPABASE_URL vazio ou inválido)'
  } catch {
    return '(VITE_SUPABASE_URL inválido)'
  }
}

/** Ordem: env fixo, ou ADM e adm (API/caso podem não coincidir com o `id` em storage.buckets). */
export function getAdmNotasStorageBucketCandidates(): string[] {
  if (ADM_NOTAS_BUCKET_ENV) return [ADM_NOTAS_BUCKET_ENV]
  return ['ADM', 'adm']
}

export function isStorageBucketNotFoundError(err: { message?: string } | null | undefined): boolean {
  return (err?.message ?? '').toLowerCase().includes('bucket not found')
}

export function formatAdmNotaStorageDownloadError(lastError: { message?: string } | null | undefined): string {
  if (isStorageBucketNotFoundError(lastError)) {
    return (
      'O Storage não encontrou o bucket. Host da API usado nesta build: ' +
      `${getSupabaseHostFromEnvForHelp()}. ` +
      'Confirme se VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY são do **mesmo** projeto em que o bucket foi criado. ' +
      'No Supabase, execute: SELECT id, name FROM storage.buckets; e defina no deploy VITE_ADM_NOTAS_STORAGE_BUCKET com o `id` exato (se necessário, ex.: ADM ou adm). ' +
      'É necessário **novo build** do frontend após alterar as variáveis de ambiente.'
    )
  }
  return (lastError?.message ?? '').trim() || 'Não foi possível gerar o link do documento.'
}

/** Caminho no Storage: `file_name_storage` se preenchido; se vazio/null, usa `file_name`. */
export function getAdmNotaDownloadObjectKey(
  fileNameStorage: string | null | undefined,
  fileName: string | null | undefined
): string | null {
  const s = (fileNameStorage?.trim() || fileName?.trim() || '')
  return s || null
}

export function hasAdmNotaDownloadKey(
  fileNameStorage: string | null | undefined,
  fileName: string | null | undefined
): boolean {
  return Boolean(getAdmNotaDownloadObjectKey(fileNameStorage, fileName))
}

export function filenameFromStorageObjectKey(key: string): string {
  const t = key.trim()
  if (!t) return 'documento'
  const parts = t.split('/').filter(Boolean)
  return parts[parts.length - 1] || 'documento'
}

export function sanitizeAdmDownloadFileName(name: string): string {
  const n = name.trim() || 'documento'
  return n.replace(/[/\\?%*:|"<>]/g, '_').slice(0, 200)
}

/** Nome sugerido ao gravar o ficheiro local: `file_name` se existir, senão basename de `file_name_storage`. */
export function getAdmNotaSaveAsFileName(
  fileNameStorage: string | null | undefined,
  fileName: string | null | undefined
): string {
  if (fileName?.trim()) {
    return filenameFromStorageObjectKey(fileName)
  }
  if (fileNameStorage?.trim()) {
    return filenameFromStorageObjectKey(fileNameStorage)
  }
  return 'documento'
}

export async function triggerAdmFileDownload(
  signedUrl: string,
  fileNameOnDisk: string
): Promise<void> {
  const downloadName = sanitizeAdmDownloadFileName(fileNameOnDisk)
  try {
    const res = await fetch(signedUrl)
    if (!res.ok) {
      throw new Error(`Falha ao baixar (HTTP ${res.status})`)
    }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    try {
      const a = document.createElement('a')
      a.href = url
      a.download = downloadName
      a.rel = 'noopener'
      a.target = '_blank'
      document.body.appendChild(a)
      a.click()
      a.remove()
    } finally {
      URL.revokeObjectURL(url)
    }
  } catch {
    window.open(signedUrl, '_blank', 'noopener,noreferrer')
  }
}

const DEFAULT_SIGNED_URL_TTL = 600

/**
 * Gera URL assinada no bucket ADM (candidatos em env) e inicia o download.
 * @param storageFolderPrefix Pasta dentro do bucket, ex. `notas` ou `ORCAMENTOS` (sem barra final).
 */
export async function runAdmStorageSignedDownload(
  supabase: SupabaseClient,
  params: {
    fileNameStorage: string | null | undefined
    fileName: string | null | undefined
    storageFolderPrefix: string
    signedUrlTtlSec?: number
  }
): Promise<{ ok: true } | { ok: false; message: string }> {
  const { fileNameStorage, fileName, storageFolderPrefix } = params
  const ttl = params.signedUrlTtlSec ?? DEFAULT_SIGNED_URL_TTL
  const prefix = storageFolderPrefix.replace(/\/+$/, '').trim()
  if (!prefix) {
    return { ok: false, message: 'Prefixo de pasta no Storage inválido.' }
  }
  const prefixWithSlash = `${prefix}/`
  const key = getAdmNotaDownloadObjectKey(fileNameStorage, fileName)
  if (!key) {
    return {
      ok: false,
      message: 'Defina file_name ou file_name_storage no cabeçalho para localizar o arquivo no Storage.',
    }
  }
  try {
    const path = key.startsWith(prefixWithSlash) ? key : `${prefixWithSlash}${key.replace(/^\//, '')}`
    const candidates = getAdmNotasStorageBucketCandidates()
    let lastError: { message?: string } | null | undefined
    for (const bucket of candidates) {
      const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, ttl)
      if (!error && data?.signedUrl) {
        await triggerAdmFileDownload(data.signedUrl, getAdmNotaSaveAsFileName(fileNameStorage, fileName))
        return { ok: true }
      }
      lastError = error
      if (error && !isStorageBucketNotFoundError(error)) {
        return {
          ok: false,
          message:
            (error as { message?: string }).message?.trim() ?? 'Não foi possível gerar o link do documento.',
        }
      }
    }
    return { ok: false, message: formatAdmNotaStorageDownloadError(lastError) }
  } catch (err) {
    return {
      ok: false,
      message: err instanceof Error ? err.message : 'Erro inesperado ao baixar o documento.',
    }
  }
}
